import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { Button } from '../../../components/ui/Button.jsx';
import { Badge } from '../../../components/ui/Badge.jsx';
import { productsApi } from '../../../api/products.js';
import { t } from '../../../lib/i18n.js';

import { VariantCreateDialog } from './VariantCreateDialog.jsx';

const STATUS_TONE = { active: 'success', draft: 'neutral', archived: 'warning' };

function attrLabel(attrs) {
  if (!attrs) return null;
  const entries = Object.entries(attrs);
  if (!entries.length) return null;
  return entries.map(([k, v]) => `${k}: ${v}`).join(' · ');
}

/**
 * Variants manager shown inside a base product's form. Lists the variants
 * (each a real product with its own SKU/price/stock), and lets the operator
 * add a new one or detach an existing one (which keeps its inherited content).
 * Editing a variant's own fields is done by opening it from the products list.
 */
export function ProductVariantsSection({ base }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [addOpen, setAddOpen] = useState(false);

  const variantsQuery = useQuery({
    queryKey: ['products', base._id, 'variants'],
    queryFn: () => productsApi.listVariants(base._id),
    enabled: Boolean(base && base._id)
  });

  const detach = useMutation({
    mutationFn: (id) => productsApi.detachVariant(id),
    onSuccess: () => {
      toast.success('Variante scollegata (resta come prodotto indipendente).');
      queryClient.invalidateQueries({ queryKey: ['products', base._id, 'variants'] });
      queryClient.invalidateQueries({ queryKey: ['products'] });
    },
    onError: (err) => toast.error(err.message || t('common.error'))
  });

  const variants = variantsQuery.data?.items || [];

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-ink">Varianti</h3>
          <p className="text-xs text-ink-muted">Stesso prodotto in formati/gusti diversi. Ognuna ha SKU, prezzo e foto propri; eredita descrizione e ingredienti.</p>
        </div>
        <Button type="button" variant="secondary" onClick={() => setAddOpen(true)}>Aggiungi variante</Button>
      </div>

      {variantsQuery.isLoading ? (
        <p className="text-sm text-ink-muted">Caricamento...</p>
      ) : variants.length === 0 ? (
        <p className="text-sm text-ink-faint">Nessuna variante. Usa "Aggiungi variante" per il primo formato/gusto.</p>
      ) : (
        <ul className="flex flex-col divide-y divide-border rounded-md border border-border">
          {variants.map((v) => (
            <li key={v._id} className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-surface-subtle">
              <button
                type="button"
                onClick={() => navigate(`/admin/products/${v._id}/edit`)}
                className="min-w-0 flex-1 text-left"
                title="Apri la variante"
              >
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm text-ink hover:underline">{v.name}</span>
                  <Badge tone={STATUS_TONE[v.status] || 'neutral'}>{v.status}</Badge>
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
                  <code className="rounded bg-border/40 px-1 py-0.5">{v.sku}</code>
                  {attrLabel(v.variantAttributes) && <span>{attrLabel(v.variantAttributes)}</span>}
                  {v.price?.amount != null && <span>{Number(v.price.amount).toFixed(2)} €</span>}
                </div>
              </button>
              <button
                type="button"
                className="shrink-0 text-xs text-ink-muted hover:text-danger disabled:opacity-50"
                disabled={detach.isPending}
                onClick={() => detach.mutate(v._id)}
              >
                Scollega
              </button>
            </li>
          ))}
        </ul>
      )}

      <VariantCreateDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        base={base}
        onCreated={() => variantsQuery.refetch()}
      />
    </section>
  );
}
